import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  TextInput,
  Button,
  Text,
  Container,
  Stack,
  Center,
  useMantineColorScheme,
  Box,
  Image,
  ActionIcon,
  Alert
} from '@mantine/core';
import { IconArrowLeft, IconMail, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import authService from '../services/authService';
import enarmLogo from '../assets/enarm_logo.png';
import { getGlassmorphismInputStyles, glassmorphismInputProps } from '../styles/inputStyles';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await authService.forgotPassword(email);

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.message || 'Error al enviar el email');
      }
    } catch (err: any) {
      console.error('Error en forgot password:', err);
      setError('Error al procesar la solicitud. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: colorScheme === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Textura de fondo */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: colorScheme === 'dark'
            ? `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23374151" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`
            : `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23ddd8d1" stroke-width="0.5" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`,
          opacity: 0.5,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Back Button */}
      <ActionIcon
        onClick={() => navigate('/login')}
        variant="light"
        size="lg"
        radius="xl"
        style={{
          position: 'absolute',
          top: '2rem',
          left: '2rem',
          zIndex: 10,
          backgroundColor: colorScheme === 'dark'
            ? 'rgba(30, 41, 59, 0.8)'
            : 'rgba(247, 243, 238, 0.8)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
          boxShadow: colorScheme === 'dark'
            ? '0 4px 16px rgba(0, 0, 0, 0.3)'
            : '0 4px 16px rgba(0, 0, 0, 0.1)',
        }}
      >
        <IconArrowLeft size={20} />
      </ActionIcon>

      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
      }}>
        <Container size={420} style={{ width: '100%', maxWidth: '420px' }}>
          <Paper
            radius="xl"
            p="lg"
            withBorder
            style={{
              width: '100%',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(247, 243, 238, 0.6)',
              backdropFilter: 'blur(40px) saturate(200%)',
              WebkitBackdropFilter: 'blur(40px) saturate(200%)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
              boxShadow: colorScheme === 'dark'
                ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 20px rgba(0, 0, 0, 0.3)'
                : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
            }}
          >
            <Stack gap="lg">
              <Center>
                <Stack align="center" gap="sm">
                  <Image
                    src={enarmLogo}
                    alt="ENARM360 Logo"
                    height={65}
                    fit="contain"
                    style={{
                      borderRadius: '12px',
                      maxWidth: '200px',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate('/')}
                  />
                  <Text size="xl" fw={700} ta="center" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                    ¿Olvidaste tu contraseña?
                  </Text>
                  <Text size="sm" c="dimmed" ta="center">
                    Ingresa tu email y te enviaremos un enlace para recuperarla
                  </Text>
                </Stack>
              </Center>

              {!success ? (
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <TextInput
                      placeholder="correo@ejemplo.com"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      leftSection={<IconMail size={16} />}
                      {...glassmorphismInputProps}
                      styles={getGlassmorphismInputStyles(colorScheme)}
                    />

                    {error && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        radius="xl"
                        variant="light"
                        style={{
                          backgroundColor: colorScheme === 'dark'
                            ? 'rgba(239, 68, 68, 0.1)'
                            : 'rgba(239, 68, 68, 0.05)',
                          backdropFilter: 'blur(20px) saturate(180%)',
                          border: `1px solid rgba(239, 68, 68, 0.2)`,
                        }}
                      >
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      loading={loading}
                      disabled={!email}
                      size="md"
                      radius="xl"
                      variant="light"
                      style={{
                        marginTop: '0.5rem',
                        backgroundColor: colorScheme === 'dark'
                          ? 'rgba(30, 41, 59, 0.8)'
                          : 'rgba(247, 243, 238, 0.8)',
                        backdropFilter: 'blur(40px) saturate(200%)',
                        border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(221, 216, 209, 0.8)'}`,
                        color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                        fontFamily: 'Space Grotesk, Inter, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
                    </Button>
                  </Stack>
                </form>
              ) : (
                <Alert
                  icon={<IconCheck size={16} />}
                  color="green"
                  radius="xl"
                  variant="light"
                  style={{
                    backgroundColor: colorScheme === 'dark'
                      ? 'rgba(34, 197, 94, 0.1)'
                      : 'rgba(34, 197, 94, 0.05)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    border: `1px solid rgba(34, 197, 94, 0.2)`,
                  }}
                >
                  <Stack gap="xs">
                    <Text fw={600}>Email enviado exitosamente</Text>
                    <Text size="sm">
                      Si el correo electrónico está registrado, recibirás un enlace para restablecer tu contraseña.
                      Revisa tu bandeja de entrada y spam.
                    </Text>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => navigate('/login')}
                      mt="xs"
                    >
                      Volver al inicio de sesión
                    </Button>
                  </Stack>
                </Alert>
              )}

              <Center>
                <Text size="sm" c="dimmed">
                  ¿Recordaste tu contraseña?{' '}
                  <Text
                    component="span"
                    size="sm"
                    c="blue"
                    style={{ cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => navigate('/login')}
                  >
                    Inicia sesión
                  </Text>
                </Text>
              </Center>
            </Stack>
          </Paper>
        </Container>
      </div>
    </Box>
  );
};

export default ForgotPasswordPage;
